/**
 * @fileoverview 채널에 종속된 모든 메시지를 조회합니다. 이 함수는 두가지 용도로 쓰입니다.
 * 1. 채널 목록에서 채널을 선택했을때, 이 채널의 모든 메시지 조회
 * 2. sendMessage 함수를 호출한 이후 서버와 채널 상태를 동기화 하기 위해 exponential backoff 로직을 통해 메시지를 조회
 */
import { GetMessagesRequest, Range } from "@daangn/idl/rocketchat/api/api";
import { normalize } from "@data-client/normalizr";
import { first, isNil, last } from "lodash";
import { store } from "~/rocketchat/redux/store";
import { sendRocketchatRequestWithPromise } from "~/rocketchat/rocketChatClient";
import type { NormalizedResult } from "~/rocketchat/types";
import type { User } from "~/rocketchat/user/user.entity";
import { ChatMessage } from "../chatMessage.entity";
import { chatMessageGroupByChannelIdSelectors } from "../chatMessageSlice";

const MAX_RETRY_COUNT = 3;
const retryCountMap = new Map<string, number>();

export async function getMessages(
  {
    channelId,
    limit,
    range,
  }: {
    channelId: string;
    limit?: number;
    range?: Range;
  },
  socket: WebSocket,
) {
  const state = store.getState();
  const currentLastMessageId = last(
    chatMessageGroupByChannelIdSelectors(state.chatMessage)[channelId],
  )?.id;

  const response = await sendRocketchatRequestWithPromise({
    socket,
    chatRequest: {
      name: "GetMessagesRequest",
      get_messages: GetMessagesRequest.create({
        /** Required. The ID of the channel */
        channel_id: channelId,
        /**
         * @see https://www.notion.so/daangn/for-1e028c3a9f8f809098e0f8364b10c809?pvs=4#1e028c3a9f8f801f9ffde2afa963eea3
         * Required. The ID of the message
         * */
        message_id: currentLastMessageId,
        /**
         * Required. 최대 message 수
         * Max: 100
         */
        limit: limit ?? 100,
        // /** Message 기준 operator */
        range: range ?? Range.LESS_EQUAL,
      }),
    },
  });

  const getMessagesResponse = response.get_messages;

  if (isNil(getMessagesResponse)) {
    throw new Error("getMessages: response is nil");
  }

  /**
   * @description API 응답에서 메시지는 만들어진 시간 기준 내림차순 정렬되어 있으므로 첫 번째 메시지가 최신 메시지임
   */
  const lastMessageFromResponse = first(getMessagesResponse.messages);

  if (isNil(lastMessageFromResponse)) {
    throw new Error("getMessages: lastMessageFromResponse is nil");
  }

  if (lastMessageFromResponse.id === currentLastMessageId) {
    retryCountMap.delete(channelId);

    const normalizedChatMessage = normalize<
      (typeof ChatMessage)[],
      {
        ChatMessage: NormalizedResult<ChatMessage>;
        User: NormalizedResult<User>;
      }
    >([ChatMessage], getMessagesResponse.messages);

    return {
      messages: normalizedChatMessage.entities.ChatMessage,
      raw: getMessagesResponse,
    };
  }

  const retryCount = retryCountMap.get(channelId) ?? 0;

  if (retryCount > MAX_RETRY_COUNT) {
    retryCountMap.delete(channelId);

    // TODO: message 리스트를 최신화 못했을 경우 에러 처리 필요
    return;
  }

  /**
   * 현재 redux에 저장된 마지막 메시지 id와 비교해서 다르면 다시 요청
   */
  const timeoutId = setTimeout(
    () => {
      getMessages({ channelId }, socket);
      clearTimeout(timeoutId);
    },
    2 ** retryCount * 1000,
  );
  retryCountMap.set(channelId, retryCount + 1);
}
